import * as SFMLWeb from "@flipp/sfml-web";
import tippy, { followCursor } from "tippy.js";

const debounce = (f, ms) => {
  let isCooldown = false;
  return function () {
    if (isCooldown) return;
    f.apply(this, arguments);
    isCooldown = true;
    setTimeout(() => (isCooldown = false), ms);
  };
};
export class SFMLController {
  constructor(id) {
    this.data = null;
    this.minValue = 0;
    this.maxValue = 0;
    this.state = null;
    this.sfmlScrollTop = 0;
    this.storefrontId = `#${id}`;
    this.switchClassName = !this.storefrontId.includes("fullscreen")
      ? ".toggle-heatmap.ant-switch"
      : ".toggle-fullscrean-heatmap.ant-switch";
  }

  setStorefront(storefront) {
    this.storefront = storefront;

    const self = this;
    this.storefront.addEventListener("SFML_STOREFRONT_VIEW", (e) => {
      const switchHeat = document.querySelector(this.switchClassName).checked;
      document
        .querySelector(`${this.storefrontId} sfml-storefront`)
        .setAttribute("title", "");
      const { visibleItems } = e.detail;
      const setHeatElement = this.setHeatElement.bind(self);
      switchHeat
        ? visibleItems.forEach(setHeatElement)
        : this.removeAllHeatmapElements();
    });

    document.querySelector(this.storefrontId).onscroll = this.sfmlRender();
    document.querySelector(this.switchClassName).onclick = this.sfmlRender();
  }

  sfmlRender = () =>
    debounce(function () {
      //to ensure everything is painted
      setTimeout(() => {
        SFMLWeb.ViewportObserver.shared.resize();
        SFMLWeb.ViewportObserver.shared.notifyScroll({
          isScrolledByMethod: true
        });
      }, 100);
    }, 50);

  removeAllHeatmapElements = () => {
    document.querySelectorAll(".heatmap-element").forEach((el) => el.remove());
  };

  setState(state) {
    this.state = state;
  }

  getState() {
    return this.state;
  }

  prepareTooltipAndHeat() {
    if (!this.data) {
      this.prepareTooltipData();
      this.prepareHeatData();
    }
  }

  getItemLink(item) {
    const storefrontDiv = document.querySelector(this.storefrontId);
    const itemImage = storefrontDiv.querySelector(
      `[source-id="${item.itemId}"]`
    );
    const itemLink = itemImage.parentElement.querySelector(
      `[data-item-id="${item.itemId}"]`
    );
    return itemLink;
  }

  setHeatElement(item) {
    if (this.data[item.itemId]) {
      const itemLink = this.getItemLink(item);
      const { label } = this.data[item.itemId];
      this.insertHeatElement(itemLink, item, label);
    }
  }

  insertHeatElement(parent, item, label) {
    if (!this.exists(item)) {
      const el = this.getHeatElement(item);
      parent.prepend(el);
      const tooltip = this.getTooltipElement(el, item, label);
      tippy(parent, {
        allowHTML: true,
        followCursor: true,
        plugins: [followCursor],
        content: `<div class="heatmap-element-tooltip">${tooltip.innerHTML}</div>`,
        delay: [150, 0]
      });
    }
  }

  exists(item) {
    const el = document.querySelector(
      `${this.storefrontId} .heatmap-element[heat-el-id="${item.itemId}"]`
    );
    return Boolean(el);
  }

  getHeatElement(item) {
    const div = document.createElement("div");
    div.setAttribute("heat-el-id", `${item.itemId}`);
    div.classList.add("heatmap-element");
    div.style.backgroundColor = this.getColor(item);
    return div;
  }

  getColor(item) {
    const itemId = parseInt(item.itemId, 10);
    const value = this.data[itemId] ? this.data[itemId].metric : 0;
    const range = this.maxValue - this.minValue;
    let ratio = range === 0 ? 0 : (value - this.minValue) / range;
    ratio = Math.min(ratio, 1);

    /* In HSL, red is hue=0, green is hue=120 and blue is hue=240. We
    want items with high value to be red, so we flip the ratio */
    const hue = (1 - ratio) * 240;
    return "hsla(" + hue + ", 100%, 50%, 0.45)";
  }

  getTooltipElement(element, item, label) {
    // main wrapper
    const wrapper = document.createElement("div");

    // title
    const title = document.createElement("span");
    title.classList.add("heatmap-element-tooltip-title");
    title.innerHTML = label;

    // metric
    const metric = document.createElement("span");
    metric.classList.add("heatmap-element-tooltip-metric");

    const metricValue = this.getPreparedMetric();
    metric.innerHTML = `${metricValue.toUpperCase()}&nbsp;&nbsp;`;

    // value
    const itemId = parseInt(item.itemId, 10);
    const itemValue = this.data[itemId] ? this.data[itemId].metric : 0;

    const value = document.createElement("span");
    value.classList.add("heatmap-element-tooltip-value");
    value.innerHTML = itemValue;

    const valuesWrapper = document.createElement("ul");
    const lineWrapper = document.createElement("li");
    lineWrapper.appendChild(metric);
    lineWrapper.appendChild(value);
    valuesWrapper.appendChild(lineWrapper);

    wrapper.appendChild(title);
    wrapper.appendChild(valuesWrapper);

    return wrapper;
  }

  prepareHeatData() {
    const state = this.getState().analytics_metric;
    const metric = this.getPreparedMetric();

    const metricValues = state.flyerItems.map((item) => item[metric]);

    /* To normalize the heatmap, we treat the third highest value as
    the top value*/
    this.maxValue = metricValues.sort((a, b) => b - a)[2];
    this.minValue = metricValues.reduce((a, b) => Math.min(a, b), 0);
  }

  /**
   * @todo API returns views instead of item_views
   */
  getPreparedMetric() {
    const metric = this.getState().analytics.metrics_container_tab;
    if (metric === "item_views") {
      return "views";
    }

    return metric;
  }

  prepareTooltipData() {
    const state = this.getState().analytics_metric;
    const metric = this.getPreparedMetric();
    this.data = {};
    state.flyerItems.forEach((item) => {
      if (state.group_by === "section") {
        this.data[item.orig_flyer_item_page_position] = {
          metric: item[metric],
          label: `Section ${item.orig_flyer_item_page_position}`
        };
      } else {
        this.data[item.sfml_item_id] = {
          metric: item[metric],
          label: item.orig_flyer_item_name
        };
      }
    });
  }
}

export const renderStorefront = (xmlNode, state, id) => {
  const storefrontDiv = document.querySelector(`#${id}`);
  if (!storefrontDiv || !(storefrontDiv instanceof HTMLElement)) return;

  storefrontDiv.innerHTML = "";

  if (state.analytics_metric.group_by === "section") {
    const flyerSources = xmlNode.querySelectorAll("flyer-source");
    flyerSources.forEach((flyerSource, i) => {
      const areas = flyerSource.querySelectorAll("area");

      let combineCoords = [null, null, null, null];

      areas.forEach((area, j) => {
        const coords = (area.attributes["rect"].value.split(" ") || []).map(
          (x) => Number(x)
        );

        if (coords.length !== 4) return;

        if (!combineCoords[0] || combineCoords[0] > coords[0])
          combineCoords[0] = coords[0];
        if (!combineCoords[1] || combineCoords[1] < coords[1])
          combineCoords[1] = coords[1];
        if (!combineCoords[2] || combineCoords[2] < coords[2])
          combineCoords[2] = coords[2];
        if (!combineCoords[3] || combineCoords[3] > coords[3])
          combineCoords[3] = coords[3];

        if (j === areas.length - 1) {
          const section = i + 1;
          const rect = combineCoords.join(" ");

          area.attributes["source-id"].value = section;
          area.attributes["source-id"].nodeValue = section;
          area.attributes["source-id"].textContent = section;

          area.attributes["rect"].value = rect;
          area.attributes["rect"].nodeValue = rect;
          area.attributes["rect"].textContent = rect;
        } else {
          area.remove();
        }
      });
    });
  }

  const storefrontData = SFMLWeb.parse(xmlNode);
  if (!storefrontData) return;

  const storefrontController = new SFMLController(id);
  storefrontController.setState(state);

  const storefront = SFMLWeb.render(
    storefrontData,
    storefrontController,
    null,
    storefrontDiv
  );

  storefrontController.prepareTooltipAndHeat();

  storefront.startTracking();

  return storefront;
};



// WEBPACK FOOTER //
// ./client/modules/AnalyticsMetric/components/SFML/sfml.js